import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import App from './app.tsx';
import { PhotoViewerBoundary } from './components/photo-viewer.tsx';
import { PdfDownloadIndicator } from './components/pdf-download.tsx';
import { SendOutcomeIndicator } from './components/send-outcome.tsx';
import { SessionProvider } from './state/session.tsx';
import { pruneDrafts } from './state/draft-store.ts';
import { pruneAdoptedWalkthroughs } from './state/local-walkthrough.ts';
import { offlineQueryStructuralSharing } from './state/offline-query.ts';
import { AdminReadTimeoutError } from './api/admin.ts';
import './i18n/index.ts';
import './styles/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The app is a single-operator tool; refetching on every window focus is
      // more noise than it is worth.
      refetchOnWindowFocus: false,
      // Let query functions run while offline. Selective GETs can then fall
      // back to their exact bounded IndexedDB response; uncached requests fail
      // normally instead of sitting indefinitely in TanStack's paused state.
      networkMode: 'always',
      structuralSharing: offlineQueryStructuralSharing,
      staleTime: 30_000,
      // A timed-out admin read already spent its 15-second budget. Show its
      // retry button then; keep one automatic retry for ordinary read errors.
      retry: (failureCount, error) => !(error instanceof AdminReadTimeoutError) && failureCount < 1,
    },
  },
});

// Housekeeping for local drafts left behind by finished quotes (spec §17.1),
// and for the pointers left behind by walkthroughs that were started offline
// and have since been handed over.
void pruneDrafts();
void pruneAdoptedWalkthroughs();

/**
 * The offline shell.
 *
 * Registered after load rather than during it, so fetching and installing the
 * worker never competes with the bundles the first paint is waiting on — the
 * benefit is entirely for later visits, and nobody should pay for it now.
 *
 * Production only: under `vite dev` the modules are served unbundled and
 * rewritten on every edit, and a worker caching them is a morning spent
 * wondering why a change did not take.
 */
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // Failure is not worth reporting: an unsupported browser, a private
    // window, or a policy that forbids workers all land here, and every one
    // of them leaves an app that works exactly as it did before.
    void navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}

const container = document.getElementById('root');
if (!container) throw new Error('Root element not found');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionProvider>
          <PhotoViewerBoundary />
          <App />
          {/* Outside the routes on purpose: a PDF started on one screen keeps
              being fetched after a navigation, and the indicator has to
              outlive the page that started it. */}
          <PdfDownloadIndicator />
          <SendOutcomeIndicator />
        </SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
